import EventCenter from './event_center'
import { appInstanceMap } from '../create_app'
import { defer } from '../libs/utils'

const eventCenter = new EventCenter()

function formatEventName (appName: string, fromBaseApp: boolean): string {
  if (typeof appName !== 'string' || !appName) return ''
  return fromBaseApp ? `__from_base_app_${appName}__` : `__from_micro_app_${appName}__`
}

/**
 * 设置全局数据
 * 全局数据会发送给包括自己在内的所有应用
 */
class EventCenterForGlobal {
  addGlobalDataListener (cb: CallableFunction): void {
    eventCenter.on('global', cb)
  }

  removeGlobalDataListener (cb: CallableFunction): void {
    if (typeof cb === 'function') {
      eventCenter.off('global', cb)
    }
  }

  setGlobalData (data: Record<PropertyKey, unknown>): void {
    eventCenter.dispatch('global', data)
  }

  clearGlobalDataListener (): void {
    eventCenter.off('global')
  }
}

/**
 * 基座应用发送的数据和某个应用绑定，不会泄漏到其它应用
 */
export class EventCenterForBaseApp extends EventCenterForGlobal {
  // 添加监听事件
  addDataListener (appName: string, cb: CallableFunction): void {
    eventCenter.on(formatEventName(appName, false), cb)
  }

  // 解除监听
  removeDataListener (appName: string, cb: CallableFunction): void {
    if (typeof appName === 'string' && typeof cb === 'function') {
      eventCenter.off(formatEventName(appName, false), cb)
    }
  }

  // 主动获取子应用或基座传递的数据
  getData (appName: string, fromBaseApp = false): Record<PropertyKey, unknown> | null {
    return eventCenter.getData(formatEventName(appName, fromBaseApp))
  }

  // 发送数据
  setData (appName: string, data: Record<PropertyKey, unknown>): void {
    eventCenter.dispatch(formatEventName(appName, true), data)
  }

  // 清空监听某个应用的函数
  clearDataListener (appName: string): void {
    eventCenter.off(formatEventName(appName, false))
  }
}

/**
 * 1、子应用只能接受来自基座应用的数据
 * 2、子应用只能发送数据到基座应用
 */
export class EventCenterForMicroApp extends EventCenterForGlobal {
  appName: string
  constructor (appName: string) {
    super()
    this.appName = appName
  }

  // 监听基座应用发送的数据
  addDataListener (cb: CallableFunction): void {
    eventCenter.on(formatEventName(this.appName, true), cb)
  }

  // 解除绑定
  removeDataListener (cb: CallableFunction): void {
    if (typeof cb === 'function') {
      eventCenter.off(formatEventName(this.appName, true), cb)
    }
  }

  // 主动获取来自基座的数据
  getData (): Record<PropertyKey, unknown> | null {
    return eventCenter.getData(formatEventName(this.appName, true))
  }

  // 向基座应用发送数据
  dispatch (data: Record<PropertyKey, unknown>): void {
    // 一些框架(如：react16)将可冒泡事件代理到document上，为了防止基座应用元素被绑定到微应用，所以延迟发送数据
    defer(() => {
      eventCenter.dispatch(formatEventName(this.appName, false), data)

      const app = appInstanceMap.get(this.appName)
      if (app?.container && toString.call(data) === '[object Object]') {
        const event = new CustomEvent('datachange', {
          detail: {
            data,
          }
        })

        let element = app.container
        if (element instanceof ShadowRoot) {
          element = element.host as HTMLElement
        }
        element.dispatchEvent(event)
      }
    })
  }

  // 主动清空当前子应用绑定的所有监听事件
  clearDataListener (): void {
    eventCenter.off(formatEventName(this.appName, true))
  }
}
